/**
 * RevendaFormWizard.jsx
 * Formulario de Revenda em Etapas Premium
 *
 * 6 Passos:
 * 1. Basico - Logo, Codigo, Titulo, URL
 * 2. Informacoes - Template, Telefone, Email, Documento, Endereco
 * 3. Customizacao - Cores e Background
 * 4. Apps - Google Play, Apple Store
 * 5. Favicons - Upload de icone
 * 6. Revisao
 */

import React, {
 useState, useEffect, useCallback, useMemo 
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Popover,
  Tooltip,
  Button,
} from '@mui/material';
import { ChromePicker } from 'react-color';
import {
  Business as BusinessIcon,
  Info as InfoIcon,
  Palette as PaletteIcon,
  Apps as AppsIcon,
  Photo as PhotoIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useCatch } from '../../reactHelper';
import { useAdministrator } from '../../common/util/permissions';
import { getThemePaths } from '../../common/auth/themePaths';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';
import UploadAvatar from '../../common/components/upload/UploadAvatar';
import EditAttributesAccordion from './EditAttributesAccordion';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors, onLogoDrop }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);
  const admin = useAdministrator();
  const theme = useTheme();
  const currentMode = theme?.palette?.mode || 'light';

  const getLogo = () => {
    if (data.logoPreview) {
      return { preview: data.logoPreview };
    }
    if (data.code) {
      const mode = currentMode === 'dark' ? 'dark' : 'light';
      return `/api/revendas/logo/${data.code}?theme=${mode}`;
    }
    return null;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        {/* Logo */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <UploadAvatar
            accept="image/*"
            file={getLogo()}
            maxSize={Number.MAX_SAFE_INTEGER}
            onDrop={onLogoDrop}
            caption={t('attributeResaleLogo')}
          />
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', mt: 1, color: colors.textMuted }}
          >
            {t('sharedAllowed')}<br />{t('sharedMaxSize')}
          </Typography>
        </Box>

        {/* Campos principais */}
        <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {admin && (
            <PremiumInput
              label={`${t('sharedCodeInternal')} *`}
              value={data.code || ''}
              onChange={(e) => updateData({ code: e.target.value })}
              error={!!errors?.code}
              helperText={errors?.code}
            />
          )}
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.title || ''}
            onChange={(e) => updateData({ title: e.target.value })}
            error={!!errors?.title}
            helperText={errors?.title}
          />
          <PremiumInput
            label={`${t('sharedUrl')} *`}
            value={data.url || ''}
            onChange={(e) => updateData({ url: e.target.value })}
            error={!!errors?.url}
            helperText={errors?.url}
          />
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 2: INFORMACOES DE CONTATO
// ============================================================================

const InfoStep = ({ data, updateData }) => {
  const t = useTranslation();

  const fields = [
    ['template', 'phone', 'email'],
    ['document', 'address', 'neighborhood'],
    ['city', 'state', 'postalcode'],
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {fields.map((row, idx) => (
        <Grid container spacing={3} key={idx}>
          {row.map((field) => (
            <Grid size={{ xs: 12, sm: 4 }} key={field}>
              <PremiumInput
                value={data[field] || ''}
                onChange={(e) => updateData({ [field]: e.target.value })}
                label={t(`shared${field.charAt(0).toUpperCase() + field.slice(1)}`)}
              />
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
};

// ============================================================================
// STEP 3: CUSTOMIZACAO (CORES)
// ============================================================================

const CustomizationStep = ({ data, updateData, backgroundAvailable, paths }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);
  const theme = useTheme();
  const currentMode = theme?.palette?.mode || 'light';

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentColorField, setCurrentColorField] = useState(null);

  const lightPattern = `linear-gradient(135deg, #e6f0ff 0%, #d0e1ff 25%, #ffffff 50%, #e6f0ff 100%)`;
  const darkPattern = `linear-gradient(135deg, #0a192f 0%, #0d1f3a 25%, #122b4a 50%, #0a192f 100%)`;

  const handleColorClick = (field, event) => {
    setCurrentColorField(field);
    setAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setAnchorEl(null);
    setCurrentColorField(null);
  };

  const handleColorChange = (color) => {
    updateData({ [currentColorField]: color.hex });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((num) => {
          const field = `color${num}`;
          const defaultValue = num === 3 ? '#cccccc' : '#1976d2';
          return (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={num}>
              <PremiumInput
                value={data[field] || defaultValue}
                onChange={(e) => updateData({ [field]: e.target.value })}
                label={`${t('attributeColor')} ${num}`}
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: data[field] || defaultValue,
                        marginRight: 1,
                        border: `1px solid ${colors.borderSubtle}`,
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease',
                        '&:hover': { transform: 'scale(1.1)' },
                      }}
                      onClick={(e) => handleColorClick(field, e)}
                    />
                  ),
                }}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Campo background */}
      <PremiumInput
        value={data.background || '#ffffff'}
        onChange={(e) => updateData({ background: e.target.value })}
        label={t('attributeBackground')}
        InputProps={{
          startAdornment: (
            <Tooltip
              title={
                backgroundAvailable?.light || backgroundAvailable?.dark
                  ? 'Imagem de fundo disponivel'
                  : 'Gradiente padrao'
              }
              arrow
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: tokens?.radius?.sm || '4px',
                  background:
                    (backgroundAvailable?.light && paths?.backgroundLight && currentMode === 'light')
                      ? `url(${paths.backgroundLight})`
                      : (backgroundAvailable?.dark && paths?.backgroundDark && currentMode === 'dark')
                        ? `url(${paths.backgroundDark})`
                        : (currentMode === 'dark' ? darkPattern : lightPattern),
                  backgroundSize: 'cover',
                  marginRight: 1,
                  border: `1px solid ${colors.borderSubtle}`,
                  cursor: 'pointer',
                }}
                onClick={(e) => handleColorClick('background', e)}
              />
            </Tooltip>
          ),
        }}
      />

      {/* Color picker popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleColorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <ChromePicker
          color={data[currentColorField] || '#1976d2'}
          onChange={handleColorChange}
        />
      </Popover>

      <Box
        sx={{
          p: 2,
          borderRadius: tokens?.radius?.md || '8px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('revendaColorsHelp') || 'Clique nas cores para abrir o seletor. A cor 1 e usada como primaria, cor 2 como secundaria, cor 3 para textos claros e cor 4 para destaques.'}
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 4: APLICATIVOS
// ============================================================================

const AppsStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            value={data.googleplay || ''}
            onChange={(e) => updateData({ googleplay: e.target.value })}
            label={t('sharedGooglePlay')}
            placeholder="https://play.google.com/store/apps/details?id=..."
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            value={data.applestore || ''}
            onChange={(e) => updateData({ applestore: e.target.value })}
            label={t('sharedAppleStore')}
            placeholder="https://apps.apple.com/app/..."
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          p: 2,
          borderRadius: tokens?.radius?.md || '8px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('revendaAppsHelp') || 'Links para os aplicativos nas lojas Google Play e Apple Store. Serao exibidos na pagina de login.'}
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 5: FAVICONS
// ============================================================================

const FaviconsStep = ({ data, updateData, iconPreview, onIconInput }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
        {t('sharedIconsDescription')}
      </Typography>

      <input
        accept="image/*"
        style={{ display: 'none' }}
        id="icon-upload-wizard"
        type="file"
        onChange={onIconInput}
      />
      <label htmlFor="icon-upload-wizard">
        <Button
          variant="outlined"
          component="span"
          sx={{
            borderColor: colors.accent,
            color: colors.accent,
            borderRadius: tokens?.radius?.md || '8px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: colors.accentSubtle,
              borderColor: colors.accent,
            },
          }}
        >
          {t('sharedSelectIcon')}
        </Button>
      </label>

      {iconPreview && (
        <Box>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
            {t('sharedPreview')}
          </Typography>
          <Box
            component="img"
            src={iconPreview}
            alt="Icon preview"
            sx={{
              width: 64,
              height: 64,
              objectFit: 'contain',
              borderRadius: tokens?.radius?.md || '8px',
              border: `1px solid ${colors.borderSubtle}`,
            }}
          />
        </Box>
      )}

      {data?.attributes?.iconFileName && (
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          {t('sharedCurrentFile')}: {data.attributes.iconFileName}
        </Typography>
      )}

      {/* Atributos */}
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedAttributes')}
        </Typography>
        <EditAttributesAccordion
          attributes={data.attributes}
          setAttributes={(attributes) => updateData({ attributes })}
          definitions={{}}
          hideAccordion
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 6: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const ColorPreview = ({ label, color }) => (
    <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: color,
            border: `1px solid ${colors.borderSubtle}`,
          }}
        />
        <Typography variant="body2" sx={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '12px' }}>
          {color}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      {/* Dados Basicos */}
      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedCodeInternal')} value={data.code} />
        <InfoRow label={t('sharedName')} value={data.title} />
        <InfoRow label={t('sharedUrl')} value={data.url} />
      </Box>

      {/* Cores */}
      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('poiAppearance') || 'Customizacao'}
        </Typography>
        <ColorPreview label={`${t('attributeColor')} 1`} color={data.color1 || '#1976d2'} />
        <ColorPreview label={`${t('attributeColor')} 2`} color={data.color2 || '#1976d2'} />
        <ColorPreview label={`${t('attributeColor')} 3`} color={data.color3 || '#cccccc'} />
        <ColorPreview label={`${t('attributeColor')} 4`} color={data.color4 || '#1976d2'} />
      </Box>

      {/* Contato */}
      {(data.phone || data.email || data.city) && (
        <Box
          sx={{
            backgroundColor: colors.surfaceElevated,
            borderRadius: tokens?.radius?.lg || '12px',
            border: `1px solid ${colors.borderSubtle}`,
            p: 2.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: colors.textPrimary,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '11px',
              letterSpacing: '0.05em',
              mb: 2,
            }}
          >
            {t('sharedInfo')}
          </Typography>
          <InfoRow label={t('sharedPhone')} value={data.phone} />
          <InfoRow label={t('sharedEmail')} value={data.email} />
          <InfoRow label={t('sharedCity')} value={data.city} />
          <InfoRow label={t('sharedState')} value={data.state} />
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RevendaFormWizard = ({ revenda, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const admin = useAdministrator();

  const [backgroundAvailable, setBackgroundAvailable] = useState({ light: false, dark: false });
  const [iconFile, setIconFile] = useState(null);
  const [wizardData, setWizardData] = useState(null);

  // Paths baseados nos dados atuais
  const paths = useMemo(() => getThemePaths(wizardData || revenda), [wizardData?.code, wizardData?.background, revenda]);

  // Checagem de imagem de fundo
  const checkImage = useCallback((src) => {
    return new Promise((resolve) => {
      if (!src) return resolve(false);
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });
  }, []);

  const verifyBackgrounds = useCallback(async () => {
    const code = wizardData?.code || revenda?.code;
    if (!code) return;
    const [lightOk, darkOk] = await Promise.all([
      checkImage(paths?.backgroundLight),
      checkImage(paths?.backgroundDark),
    ]);
    setBackgroundAvailable({ light: lightOk, dark: darkOk });
  }, [wizardData?.code, revenda?.code, paths?.backgroundLight, paths?.backgroundDark, checkImage]);

  useEffect(() => {
    verifyBackgrounds();
  }, [verifyBackgrounds]);

  const handleLogoDrop = useCatch(async (acceptedFiles, currentData, updateDataFn) => {
    if (!currentData?.id || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    const originalFilename = encodeURIComponent(file.name);
    const response = await fetch(`/api/revendas/${currentData.id}/logo?filename=${originalFilename}`, {
      method: 'POST',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!response.ok) throw new Error(await response.text());
    const filename = await response.text();
    const previewUrl = URL.createObjectURL(file);
    updateDataFn({ logo: filename, logoPreview: previewUrl });
  });

  const handleIconInput = useCallback((event, updateDataFn) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setIconFile(file);
        updateDataFn({
          attributes: {
            ...(wizardData?.attributes || {}),
            iconBase64: base64,
            iconFileName: file.name,
          },
        });
      };
      reader.readAsDataURL(file);
    }
  }, [wizardData?.attributes]);

  const getIconPreview = () => {
    if (iconFile) return URL.createObjectURL(iconFile);
    if (wizardData?.attributes?.iconBase64) return wizardData.attributes.iconBase64;
    return null;
  };

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardRevendaBasicDescription') || 'Logo e dados principais',
      icon: <BusinessIcon sx={{ fontSize: 18 }} />,
      component: (props) => (
        <BasicInfoStep
          {...props}
          onLogoDrop={(files) => handleLogoDrop(files, props.data, props.updateData)}
        />
      ),
      validate: (data) => {
        const errors = {};
        if (admin && !data.code?.trim()) errors.code = t('sharedRequired');
        if (!data.title?.trim()) errors.title = t('sharedRequired');
        if (!data.url?.trim()) errors.url = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.title },
        { label: t('sharedUrl'), value: data.url },
      ],
    },
    {
      label: t('sharedInfo'),
      title: t('sharedInfo'),
      description: t('wizardRevendaInfoDescription') || 'Telefone, email e endereco',
      icon: <InfoIcon sx={{ fontSize: 18 }} />,
      component: InfoStep,
      optional: true,
    },
    {
      label: t('poiAppearance') || 'Cores',
      title: t('poiAppearance') || 'Customizacao',
      description: t('wizardRevendaColorsDescription') || 'Cores e background',
      icon: <PaletteIcon sx={{ fontSize: 18 }} />,
      component: (props) => {
        // Atualiza wizardData para paths
        useEffect(() => {
          setWizardData(props.data);
        }, [props.data]);

        return (
          <CustomizationStep
            {...props}
            backgroundAvailable={backgroundAvailable}
            paths={paths}
          />
        );
      },
      optional: true,
    },
    {
      label: t('sharedApps'),
      title: t('sharedApps'),
      description: t('wizardRevendaAppsDescription') || 'Links das lojas',
      icon: <AppsIcon sx={{ fontSize: 18 }} />,
      component: AppsStep,
      optional: true,
    },
    {
      label: 'Favicons',
      title: 'Favicons',
      description: t('wizardRevendaFaviconsDescription') || 'Icones e atributos',
      icon: <PhotoIcon sx={{ fontSize: 18 }} />,
      component: (props) => (
        <FaviconsStep
          {...props}
          iconPreview={getIconPreview()}
          onIconInput={(e) => handleIconInput(e, props.updateData)}
        />
      ),
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = revenda?.id ? `/api/revendas/${revenda.id}` : '/api/revendas';
      const method = revenda?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const saved = await response.json();
        onSave?.(saved);
        navigate('/settings/revendas');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/revendas');
  };

  const initialData = revenda || {
    code: '',
    title: '',
    url: '',
    template: '',
    phone: '',
    email: '',
    document: '',
    address: '',
    neighborhood: '',
    city: '',
    state: '',
    postalcode: '',
    color1: '#1976d2',
    color2: '#1976d2',
    color3: '#cccccc',
    color4: '#1976d2',
    background: '#ffffff',
    googleplay: '',
    applestore: '',
    attributes: {},
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={revenda?.id ? null : 'revenda-wizard-draft'}
      completeButtonText={revenda?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default RevendaFormWizard;
